import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const HEADER_HEIGHT = 65;

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastOptions {
  persistent?: boolean;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, options?: ToastOptions) => void;
  hideToast: () => void;
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  const { t } = useTranslation();

  if (!context) {
    throw new Error(t('MustUsedWithinToastProvider'));
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [message, setMessage] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [type, setType] = useState(String);
  const [persistent, setPersistent] = useState(false);
  const [scrollY, setScrollY] = useState(window.scrollY);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toastTop = Math.max(0, HEADER_HEIGHT - scrollY);

  const hideToast = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
    setPersistent(false);
  }, []);

  const showToast = useCallback((msg: string, toastType: ToastType = 'info', options?: ToastOptions) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setMessage(msg);
    setType(toastType === 'error' ? 'danger' : toastType);
    setPersistent(options?.persistent ?? false);
    setVisible(true);

    if (!options?.persistent) {
      timerRef.current = setTimeout(() => setVisible(false), 12000);
    }
  }, []);

  const toastStyle: React.CSSProperties = {
    minWidth: '400px',
    fontSize: '1.05rem',
    ...(persistent ? { animation: 'gg-toast-pulse 1.6s ease-in-out infinite' } : null),
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {persistent && (
        <style>{`
          @keyframes gg-toast-pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.55); transform: scale(1); }
            50%      { box-shadow: 0 0 0 12px rgba(255, 255, 255, 0); transform: scale(1.015); }
          }
        `}</style>
      )}
      {children}
      {visible && (
        <div className="toast-container position-fixed end-0 p-3" style={{ top: `${toastTop}px`, zIndex: 1080 }}>
          <div className={`toast align-items-center text-white bg-${type} border-0 show`} role="alert" style={toastStyle}>
            <div className="d-flex">
              <div className="toast-body p-3">{message}</div>
              <button type="button" className="btn-close btn-close-white me-2 m-auto" aria-label="Close" onClick={hideToast}></button>
            </div>
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
};
